import React from "react";
import { Link } from "react-router-dom";
import LegalLayout from "@/components/legal/LegalLayout";
import { openCookiePreferences } from "@/components/legal/cookieConsent";
import { LEGAL } from "@/lib/config";
import SEO from "@/components/seo/SEO";

export default function PoliticaCookies() {
    return (
        <>
            <SEO
                title="Política de Cookies"
                description="Política de cookies de ASATTA. Tipos de cookies utilizadas, cómo aceptarlas, rechazarlas o configurarlas."
                path="/politica-de-cookies"
            />
            <LegalLayout
            testId="politica-cookies-page"
            eyebrow="Uso de cookies"
            title="Política de Cookies"
            updatedAt={LEGAL.updatedAt}
        >
            <p>
                Esta Política de Cookies describe qué son las cookies, qué tipos utiliza
                este sitio web y cómo puede el usuario aceptarlas, rechazarlas o
                configurarlas. Se ha elaborado conforme al artículo 22.2 de la Ley
                34/2002 (LSSI-CE), al Reglamento (UE) 2016/679 (RGPD) y a las directrices
                de la Agencia Española de Protección de Datos.
            </p>

            <h2>1. ¿Qué son las cookies?</h2>
            <p>
                Una cookie es un pequeño archivo que un sitio web instala en el navegador
                o dispositivo del usuario cuando accede a determinadas páginas. Las
                cookies permiten, entre otras cosas, almacenar y recuperar información
                sobre los hábitos de navegación del usuario o de su equipo y, dependiendo
                de la información que contengan y de la forma en que utilice su equipo,
                pueden emplearse para reconocer al usuario.
            </p>

            <h2>2. Tipos de cookies utilizadas</h2>
            <p>Este sitio web puede utilizar los siguientes tipos de cookies:</p>

            <h3>2.1. Cookies técnicas (necesarias)</h3>
            <p>
                Son aquellas que permiten al usuario navegar por el sitio web y utilizar
                sus funcionalidades básicas, como el mantenimiento de la sesión, el
                recuerdo del estado de consentimiento de cookies o el correcto
                funcionamiento del sitio. Estas cookies están exentas del deber de
                consentimiento y no pueden desactivarse.
            </p>

            <h3>2.2. Cookies analíticas</h3>
            <p>
                Son aquellas que permiten al responsable el seguimiento y análisis del
                comportamiento de los usuarios en el sitio web, con el fin de introducir
                mejoras en función del análisis de los datos de uso. Solo se activan
                previo consentimiento del usuario.
            </p>
            <p className="text-sm text-[#4A5568]">
                <strong>Nota:</strong> este sitio web está preparado para integrar
                servicios analíticos (como Google Analytics) bajo el modo de
                consentimiento de Google (Consent Mode v2). En el momento actual, dichos
                servicios pueden no encontrarse activados; cuando se integren, esta
                Política se actualizará indicando el proveedor, la finalidad concreta y
                el plazo de conservación de cada cookie.
            </p>

            <h3>2.3. Cookies de personalización</h3>
            <p>
                Son aquellas que permiten al usuario acceder al servicio con algunas
                características de carácter general predefinidas en función de una serie
                de criterios como el idioma o el tipo de navegador. Solo se activan
                previo consentimiento del usuario.
            </p>

            <h3>2.4. Cookies de terceros</h3>
            <p>
                Este sitio web puede incorporar contenidos o funcionalidades servidas
                por terceros que instalan sus propias cookies. Entre los servicios que
                podrían utilizarse en el futuro se incluyen:
            </p>
            <ul>
                <li>Servicios de mapas (por ejemplo, Google Maps).</li>
                <li>Servicios de vídeo embebido (por ejemplo, YouTube o Vimeo).</li>
                <li>Servicios de estadística y analítica (por ejemplo, Google Analytics).</li>
                <li>Servicios de comunicación y envío de correo (por ejemplo, plataformas de newsletter).</li>
            </ul>
            <p className="text-sm text-[#4A5568]">
                <strong>Nota:</strong> las cookies de terceros son gestionadas por los
                respectivos proveedores conforme a sus propias políticas de privacidad.
                Cuando se activen se identificará expresamente el proveedor concreto y
                se recabará el consentimiento del usuario.
            </p>

            <h2>3. Aceptar, rechazar y configurar las cookies</h2>
            <p>
                Al acceder al sitio web se muestra un aviso informativo que permite al
                usuario:
            </p>
            <ul>
                <li><strong>Aceptar todas las cookies:</strong> se activan todas las categorías, incluidas las analíticas, de personalización y de terceros.</li>
                <li><strong>Rechazar todas las cookies:</strong> solo se mantienen activas las cookies estrictamente necesarias.</li>
                <li><strong>Configurar preferencias:</strong> permite seleccionar de forma granular qué categorías se aceptan y cuáles no.</li>
            </ul>
            <p>
                El usuario puede modificar sus preferencias en cualquier momento haciendo
                clic en el siguiente enlace:
            </p>
            <p>
                <button
                    type="button"
                    onClick={openCookiePreferences}
                    data-testid="btn-open-cookie-prefs"
                    className="inline-flex items-center gap-2 border border-[#082050] text-[#082050] px-5 py-2.5 text-sm font-medium hover:bg-[#082050]/5"
                >
                    Configurar preferencias de cookies
                </button>
            </p>

            <h2>4. Cómo eliminar las cookies desde el navegador</h2>
            <p>
                El usuario puede en cualquier momento eliminar las cookies almacenadas y
                bloquear su instalación futura a través de la configuración de su
                navegador. A continuación se facilitan enlaces a las instrucciones
                oficiales de los principales navegadores:
            </p>
            <ul>
                <li>
                    <a
                        href="https://support.google.com/chrome/answer/95647"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                    >
                        Google Chrome
                    </a>
                </li>
                <li>
                    <a
                        href="https://support.mozilla.org/es/kb/proteccion-antirastreo-mejorada-en-firefox-para-e"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                    >
                        Mozilla Firefox
                    </a>
                </li>
                <li>
                    <a
                        href="https://support.apple.com/es-es/guide/safari/sfri11471/mac"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                    >
                        Apple Safari
                    </a>
                </li>
                <li>
                    <a
                        href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                    >
                        Microsoft Edge
                    </a>
                </li>
            </ul>
            <p className="text-sm text-[#4A5568]">
                La desactivación de determinadas cookies puede afectar al correcto
                funcionamiento del sitio web o a algunas de sus funcionalidades.
            </p>

            <h2>5. Actualización de la Política</h2>
            <p>
                ASATTA puede modificar esta Política de Cookies en función de nuevas
                exigencias legislativas, reglamentarias o con la finalidad de adaptarla
                a las instrucciones dictadas por la Agencia Española de Protección de
                Datos. Se recomienda al usuario que revise esta política con cada nueva
                visita para conocer los cambios que hayan podido introducirse.
            </p>

            <p className="mt-10 text-sm text-[#718096]">
                Consulta también nuestra{" "}
                <Link to="/politica-de-privacidad" className="underline">
                    Política de Privacidad
                </Link>{" "}y nuestro{" "}
                <Link to="/aviso-legal" className="underline">
                    Aviso Legal
                </Link>
                .
            </p>
        </LegalLayout>
        </>
    );
}
